import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/authUtils';

const ApplicationStatusSelector = ({
  recruiterId,
  companyName,
  currentStatus,
  currentAppliedDate,
  currentOfferDate,
  currentOfferResponseDate,
  onUpdate
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDateInput, setShowDateInput] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const statuses = [
    { value: null, label: 'No status yet', color: 'bg-gray-100 text-gray-700', emoji: '⚪' },
    { value: 'applied', label: 'Applied', color: 'bg-blue-100 text-blue-800', emoji: '✓' },
    { value: 'interviewing', label: 'Interviewing', color: 'bg-purple-100 text-purple-800', emoji: '📞' },
    { value: 'offered', label: 'Offered', color: 'bg-green-100 text-green-800', emoji: '🎉' },
    { value: 'accepted', label: 'Accepted', color: 'bg-green-200 text-green-900', emoji: '✅' },
    { value: 'declined', label: 'Declined', color: 'bg-red-100 text-red-800', emoji: '❌' },
    { value: 'rejected', label: 'Rejected', color: 'bg-gray-200 text-gray-700', emoji: '⛔' }
  ];

  // Statuses that require date input
  const statusesRequiringDate = ['applied', 'offered', 'accepted', 'declined', 'rejected'];

  // Get the appropriate existing date for a status
  const getExistingDateForStatus = (status) => {
    switch (status) {
      case 'applied':
        return currentAppliedDate;
      case 'offered':
        return currentOfferDate;
      case 'accepted':
      case 'declined':
        return currentOfferResponseDate;
      case 'rejected':
        return currentOfferResponseDate;
      default:
        return null;
    }
  };

  // Get modal question text based on status
  const getDatePromptText = (status) => {
    switch (status) {
      case 'applied':
        return `When did you apply to ${companyName}?`;
      case 'offered':
        return `When did ${companyName} make you an offer?`;
      case 'accepted':
        return `When did you accept the offer from ${companyName}?`;
      case 'declined':
        return `When did you decline the offer from ${companyName}?`;
      case 'rejected':
        return `When were you notified of the rejection from ${companyName}?`;
      default:
        return `Select date for ${companyName}`;
    }
  };

  const currentStatusObj = statuses.find(s => s.value === currentStatus) || statuses[0];

  const handleStatusChange = async (newStatus) => {
    // If this status requires a date and we don't have one, show date input
    if (statusesRequiringDate.includes(newStatus)) {
      const existingDate = getExistingDateForStatus(newStatus);
      if (!existingDate) {
        setPendingStatus(newStatus);
        setSelectedDate(new Date().toISOString().split('T')[0]);
        setShowDateInput(true);
        return;
      }
    }

    // Otherwise, update directly
    await updateStatus(newStatus, null);
  };

  const updateStatus = async (status, dateValue = null) => {
    setIsUpdating(true);
    try {
      const authToken = localStorage.getItem('studentAuthToken');

      // Build the request body with the appropriate date field
      const requestBody = { status };

      if (dateValue) {
        switch (status) {
          case 'applied':
            requestBody.applied_date = dateValue;
            break;
          case 'offered':
            requestBody.offer_date = dateValue;
            break;
          case 'accepted':
          case 'declined':
          case 'rejected':
            requestBody.offer_response_date = dateValue;
            break;
          default:
            break;
        }
      }

      await axios.post(
        `${API_URL}/api/student/update-application-status/${recruiterId}`,
        requestBody,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );

      // Call parent's refresh function
      if (onUpdate) {
        onUpdate();
      }

      setShowDateInput(false);
      setPendingStatus(null);
    } catch (error) {
      console.error('Error updating application status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDateConfirm = () => {
    updateStatus(pendingStatus, selectedDate);
  };

  const handleDateCancel = () => {
    setShowDateInput(false);
    setPendingStatus(null);
  };

  // Determine which date to display (most relevant for current status)
  const getDisplayDate = () => {
    switch (currentStatus) {
      case 'applied':
        return currentAppliedDate;
      case 'offered':
        return currentOfferDate;
      case 'accepted':
      case 'declined':
      case 'rejected':
        return currentOfferResponseDate;
      default:
        return null;
    }
  };

  const displayDate = getDisplayDate();

  return (
    <div className="relative">
      {/* Current Status Display */}
      <div className="flex items-center gap-2">
        <select
          value={currentStatus || ''}
          onChange={(e) => handleStatusChange(e.target.value || null)}
          disabled={isUpdating}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${currentStatusObj.color} ${
            isUpdating ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:opacity-80'
          }`}
        >
          {statuses.map(status => (
            <option key={status.value || 'none'} value={status.value || ''}>
              {status.emoji} {status.label}
            </option>
          ))}
        </select>

        {displayDate && (
          <span className="text-xs text-gray-500">
            {new Date(displayDate + 'T00:00:00').toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Date Input Modal */}
      {showDateInput && pendingStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {getDatePromptText(pendingStatus)}
            </h3>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={handleDateConfirm}
                disabled={isUpdating}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                {isUpdating ? 'Saving...' : 'Confirm'}
              </button>
              <button
                onClick={handleDateCancel}
                disabled={isUpdating}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationStatusSelector;
